import { createContext, useContext, useEffect, useState } from "react";
import { ShoppingCart } from "../components/ShoppingCart";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { toast } from "../utilities/toast";
import { useServiceAvailability } from "./ServiceAvailabilityContext";

// Constants
const STORAGE_KEYS = {
  SHOPPING_CART: "shopping-cart",
  ACTIVE_STORE: "active-store",
};

// Context
const ShoppingCartContext = createContext({});

// Custom hook for using the shopping cart context
export function useShoppingCart() {
  const context = useContext(ShoppingCartContext);
  if (!context) {
    throw new Error("useShoppingCart must be used within a ShoppingCartProvider");
  }
  return context;
}

export function ShoppingCartProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useLocalStorage(STORAGE_KEYS.SHOPPING_CART, []);
  const { isOpen: isServiceOpen, opensAt: serviceOpensAt } = useServiceAvailability();

  const cartQuantity = cartItems.reduce(
    (quantity, item) => item.quantity + quantity,
    0
  );

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  const getItemQuantity = (id) => {
    return cartItems.find((item) => item.id === id)?.quantity ?? 0;
  };

  useEffect(() => {
    if (cartItems.length < 1) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_STORE, "");
    }
  }, [cartItems]);

  const increaseCartQuantity = (id, storeId, selectedOptions) => {
    if (!isServiceOpen) {
      toast.warning(
        serviceOpensAt
          ? `Teco is currently closed. We reopen at ${serviceOpensAt}.`
          : "Teco is currently closed. Please check back soon."
      );
      return;
    }

    if (cartItems.length < 1) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_STORE, storeId);
    }

    const activeStore = localStorage.getItem(STORAGE_KEYS.ACTIVE_STORE);

    if (activeStore === storeId) {
      setCartItems((currItems) => {
        const existingItem = currItems.find((item) => item.id === id);

        if (!existingItem) {
          return [
            ...currItems,
            {
              id,
              quantity: 1,
              store: storeId,
              selectedOptions,
            },
          ];
        }

        return currItems.map((item) =>
          item.id === id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      });
      toast.success("Items added to cart successfully");
    } else {
      toast.info("First checkout from the current store.");
    }
  };

  const decreaseCartQuantity = (id) => {
    setCartItems((currItems) => {
      const item = currItems.find((item) => item.id === id);
      
      if (!item) return currItems;
      
      if (item.quantity === 1) {
        return currItems.filter((item) => item.id !== id);
      }

      return currItems.map((item) =>
        item.id === id
          ? { ...item, quantity: item.quantity - 1 }
          : item
      );
    });
  };

  const removeFromCart = (id) => {
    setCartItems((currItems) => currItems.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const value = {
    getItemQuantity,
    increaseCartQuantity,
    decreaseCartQuantity,
    removeFromCart,
    clearCart,
    openCart,
    closeCart,
    cartItems,
    cartQuantity,
  };

  return (
    <ShoppingCartContext.Provider value={value}>
      {children}
      <ShoppingCart isOpen={isOpen} />
    </ShoppingCartContext.Provider>
  );
}
